import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Datenschutz = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen w-screen bg-white dark:bg-zinc-900 transition-colors duration-300">
      <section className="w-full py-12 px-4 md:px-12 lg:px-24">
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
            {t('privacy.title')}
          </h1>
          
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {/* Einleitung */}
            <section className="mb-8">
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.intro')}
              </p>
            </section>

            {/* Verantwortlicher */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.responsible.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                Petra-Maria Mebes<br />
                Schmidstraße 27<br />
                85399 Hallbergmoos<br />
                E-Mail: <a href="mailto:info@atelierpeart.com" className="text-blue-600 dark:text-blue-400 hover:underline">info@atelierpeart.com</a>
              </p>
            </section>

            {/* Arten der verarbeiteten Daten */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.dataTypes.title')}
              </h2>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
                <li>{t('privacy.dataTypes.usage')}</li>
                <li>{t('privacy.dataTypes.meta')}</li>
                <li>{t('privacy.dataTypes.contact')}</li>
              </ul>
            </section>

            {/* Zweck der Verarbeitung */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.purposes.title')}
              </h2>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
                <li>{t('privacy.purposes.provision')}</li>
                <li>{t('privacy.purposes.contact')}</li>
                <li>{t('privacy.purposes.security')}</li>
                <li>{t('privacy.purposes.measurement')}</li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.cookies.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('privacy.cookies.description')}
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t('privacy.cookies.types.title')}
              </h3>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('privacy.cookies.types.necessary.title')}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {t('privacy.cookies.types.necessary.description')}
                  </p>
                  <ul className="list-disc pl-6 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <li>cookie_consent: {t('privacy.cookies.types.necessary.consent')}</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('privacy.cookies.types.functional.title')}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {t('privacy.cookies.types.functional.description')}
                  </p>
                  <ul className="list-disc pl-6 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <li>theme: {t('privacy.cookies.types.functional.theme')}</li>
                    <li>i18next: {t('privacy.cookies.types.functional.language')}</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('privacy.cookies.types.analytics.title')}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {t('privacy.cookies.types.analytics.description')}
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 mt-6">
                {t('privacy.cookies.management.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.cookies.management.description')}
              </p>
            </section>

            {/* Rechtsgrundlagen */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.legalBasis.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.legalBasis.description')}
              </p>
            </section>

            {/* Speicherdauer */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.retention.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.retention.description')}
              </p>
            </section>

            {/* Rechte der betroffenen Personen */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.rights.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('privacy.rights.description')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
                <li>{t('privacy.rights.access')}</li>
                <li>{t('privacy.rights.rectification')}</li>
                <li>{t('privacy.rights.erasure')}</li>
                <li>{t('privacy.rights.restriction')}</li>
                <li>{t('privacy.rights.portability')}</li>
                <li>{t('privacy.rights.objection')}</li>
                <li>{t('privacy.rights.withdrawal')}</li>
                <li>{t('privacy.rights.complaint')}</li>
              </ul>
            </section>

            {/* Hosting */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.hosting.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.hosting.description')}
              </p>
            </section>

            {/* Änderungen */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('privacy.changes.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('privacy.changes.description')}
              </p>
            </section>
          </div>

          <div className="mt-12">
            <Link 
              to="/" 
              className="text-blue-600 dark:text-blue-400 hover:underline text-lg"
            >
              ← {t('privacy.backToHome')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Datenschutz;